'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useRef } from 'react';

import { CommonVideoBlockProps } from '../../../types/schemas/objects/CommonVideoBlockProps';
import { getFallbackImgUrl } from '../../../utils/video/getFallbackImgUrl';

type Props = CommonVideoBlockProps & {
	mimeType?: string;
	src?: string;
	className?: string;
};

const NativeVideoEmbed = ({
	className,
	src,
	autoplay,
	playsinline,
	muted,
	loop,
	controls = true,
	tabIndex = 0,
	pause,
	fallbackImg,
	mimeType = 'video/mp4',
}: Props) => {
	const videoRef = useRef<HTMLVideoElement | null>(null);

	const handlePlayVideo = useCallback(async (video: HTMLVideoElement) => {
		try {
			await video.play();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.warn('NativeVideoEmbed: An error occurred on playing video attempt:', error);
		}
	}, []);

	useEffect(() => {
		const video = videoRef.current;

		if (!video) return;

		if (pause) {
			try {
				video.pause();
			} catch (error) {
				// eslint-disable-next-line no-console
				console.warn('NativeVideoEmbed: An error occurred on pausing video attempt:', error);
			}
			return;
		}

		if (autoplay) {
			handlePlayVideo(video);
		}
	}, [src, pause, autoplay, handlePlayVideo]);

	if (!src) return null;

	return (
		// eslint-disable-next-line jsx-a11y/media-has-caption
		<video
			className={classNames('absolute left-0 top-0 h-full w-full object-cover', className)}
			ref={videoRef}
			autoPlay={autoplay}
			loop={loop}
			muted={muted}
			controls={controls}
			playsInline={playsinline}
			tabIndex={tabIndex}
			disablePictureInPicture={!controls}
			poster={fallbackImg && !autoplay && controls ? getFallbackImgUrl(fallbackImg.url) : undefined}
		>
			<source src={src} type={mimeType} />
		</video>
	);
};

NativeVideoEmbed.displayName = 'NativeVideoEmbed';

export default NativeVideoEmbed;
